/** H5 支持的 i18n locale。 */
export const SUPPORTED_LANGS = ['zh-CN', 'en-US', 'zh-TW', 'th-TH'] as const

export type Locale = (typeof SUPPORTED_LANGS)[number]

/** RN 未传语言、系统语言不可识别或语言包加载失败时统一回退英语。 */
export const DEFAULT_LOCALE: Locale = 'en-US'

const APP_LANGUAGE_MAP: Record<Locale, string> = {
  'zh-CN': 'zh',
  'zh-TW': 'zh-TW',
  'en-US': 'en',
  'th-TH': 'th',
}

/** 将 RN、浏览器及旧缓存中的语言码规范化为 H5 locale。 */
export function normalizeLanguage(language?: string | null): Locale | null {
  const tag = language?.trim().replace(/_/g, '-').toLowerCase()
  if (!tag) return null

  if (tag === 'en' || tag.startsWith('en-')) return 'en-US'
  if (tag === 'th' || tag.startsWith('th-')) return 'th-TH'

  const isTraditionalChinese =
    tag === 'yue'
    || tag.startsWith('yue-')
    || tag.includes('hant')
    || /^zh-(tw|hk|mo)(-|$)/.test(tag)
  if (isTraditionalChinese) return 'zh-TW'

  if (tag === 'zh' || tag.startsWith('zh-') || tag === 'wuu' || tag.startsWith('wuu-')) {
    return 'zh-CN'
  }

  return null
}

/**
 * 语言来源优先级：RN 注入/URL → 旧缓存 → 浏览器系统语言 → 英语。
 * RN 明确传入未知语言时直接回退英语，避免被旧缓存覆盖。
 */
export function resolveLanguage(
  appLanguage?: string | null,
  storedLanguage?: string | null,
  browserLanguage?: string | null,
): Locale {
  if (appLanguage?.trim()) return normalizeLanguage(appLanguage) ?? DEFAULT_LOCALE
  return normalizeLanguage(storedLanguage) ?? normalizeLanguage(browserLanguage) ?? DEFAULT_LOCALE
}

/** 将 i18n locale 转回 RN/接口使用的短语言码。 */
export function toAppLanguage(locale: Locale): string {
  return APP_LANGUAGE_MAP[locale]
}
